import { Component, Input, Output, EventEmitter } from '@angular/core';
import { usageGradientCss } from '../../utils/usage-color.util';

/**
 * Horizontal gradient-fill usage bar — Settings > Users' Copilot usage column.
 * The track paints the full green->yellow->orange->red gradient (usageGradientCss(),
 * built from the same color stops usageColor() interpolates flat colors from — the
 * 30-day history chart's per-day bars use that flat version instead, since each
 * day there is a single independent value, not a fill-vs-remaining split). A
 * neutral mask covers everything past the current percentage, revealing only the
 * portion of the gradient that's actually been used.
 */
@Component({
  selector: 'app-usage-progress-bar',
  templateUrl: './usage-progress-bar.component.html',
  styleUrls: ['./usage-progress-bar.component.scss'],
  standalone: false,
})
export class UsageProgressBarComponent {
  @Input() usagePercent: number = 0;
  /** Square corners instead of the default pill shape — e.g. the DTR Copilot
   * panel header wants a flush-edge bar rather than Settings > Users' pill. */
  @Input() squareEnds: boolean = false;
  /** false hides the "click for 30-day history" affordance (cursor + tooltip
   * wording) for a consumer with no drill-down to offer — barClick still fires
   * on click either way, so a listener can still choose to act on it. */
  @Input() clickable: boolean = true;
  /** Overrides the default tooltip text entirely when set. */
  @Input() tooltipText: string | null = null;
  /** Slim variant (reduced height/font) for a full-width strip placement — e.g.
   * the DTR Copilot panel's header-to-chat-window divider — as opposed to
   * Settings > Users' default pill-height bar. */
  @Input() thin: boolean = false;
  /** Black label text instead of the default white-with-shadow — e.g. the DTR
   * Copilot panel's request. Scoped to this input rather than changed globally:
   * Settings > Users' bar reaches red at high usage, where black text would
   * lose contrast against the white-with-shadow scheme's whole point. */
  @Input() darkText: boolean = false;
  @Output() barClick = new EventEmitter<void>();

  get fillWidthPct(): number {
    return Math.max(0, Math.min(100, this.usagePercent ?? 0));
  }

  get gradientBackground(): string {
    return usageGradientCss();
  }

  get displayPercent(): string {
    // Capped the same way the bar's fill width is — usagePercent can genuinely
    // exceed 100 (e.g. a cap lowered mid-day against usage already accrued
    // under the old, higher cap), but showing "509%" in a small bar reads as
    // a bug rather than "over the limit."
    return `${Math.round(this.fillWidthPct)}%`;
  }

  get tooltip(): string {
    if (this.tooltipText !== null) return this.tooltipText;
    return this.clickable
      ? `${this.usagePercent}% of daily cap used — click for 30-day history`
      : `${this.usagePercent}% of daily cap used`;
  }
}
